import { useMemo } from 'react'
import { PaymentRequestQueryResponse } from '../../../../../functions/invoicing/fetch-invoicing-payment-requests'
import { PaymentRequestStatus } from '../../../../../functions/invoicing/payment-request-types'
import { FEATURE_ENABLE_INVOICING_PAYMENT_METHOD_SETTINGS } from '../../../../../constants'
import { getFeatureFromCache } from '../../../../../util/feature-cache'

/** A set of payment requests together with their total amount. */
export interface PaymentRequestCalculation {
  requests: PaymentRequestQueryResponse[]
  amountCents: number
  amountDollars: number
}

const toCalculation = (
  requests: PaymentRequestQueryResponse[],
  amountOf: (request: PaymentRequestQueryResponse) => number
): PaymentRequestCalculation => {
  const amountCents = requests.reduce(
    (sum, request) => sum + amountOf(request),
    0
  )
  return { requests, amountCents, amountDollars: amountCents / 100 }
}

/**
 * Calculates payment request metrics for the payment panel.
 *
 * Filters the payment requests into visible (sent/paid), pending (cancellable),
 * and in-transit sets, and derives the amounts used for overpayment protection.
 *
 * In-transit requests are those with an ACH direct debit payment already in
 * flight (`processingBalance > 0`). They cannot be cancelled, so they are
 * excluded from the pending/cancellable set but their committed funds are
 * subtracted when working out how much can still be collected:
 * - `overpaymentThreshold`: the amount collectible before cancellable pending
 *   requests must be cancelled (balance − in-transit − pending totals).
 * - `remainingNotInTransit`: the maximum collectible without touching in-transit
 *   funds (balance − in-transit total), clamped to a minimum of 0.
 */
export const usePaymentRequestCalculations = (
  paymentRequests: PaymentRequestQueryResponse[],
  outstandingBalance?: number
) => {
  const inTransitHandlingEnabled = getFeatureFromCache(
    FEATURE_ENABLE_INVOICING_PAYMENT_METHOD_SETTINGS
  )

  const visibleRequests = useMemo(
    () =>
      paymentRequests.filter(
        request =>
          request.status === PaymentRequestStatus.SENT ||
          request.status === PaymentRequestStatus.PAID
      ),
    [paymentRequests]
  )

  const inTransitRequestInfo = useMemo(
    () =>
      toCalculation(
        (inTransitHandlingEnabled ? visibleRequests : []).filter(
          request => (request.processingBalance ?? 0) > 0
        ),
        request => request.processingBalance ?? 0
      ),
    [visibleRequests, inTransitHandlingEnabled]
  )

  const pendingRequestInfo = useMemo(
    () =>
      toCalculation(
        visibleRequests.filter(
          request =>
            request.status === PaymentRequestStatus.SENT &&
            (!inTransitHandlingEnabled ||
              (request.processingBalance ?? 0) === 0)
        ),
        request => request.requestedAmount
      ),
    [visibleRequests, inTransitHandlingEnabled]
  )

  const calculations = useMemo(() => {
    const outstandingBalanceCents =
      outstandingBalance !== undefined ? outstandingBalance * 100 : undefined

    const overpaymentThresholdCents =
      outstandingBalanceCents !== undefined
        ? outstandingBalanceCents -
          inTransitRequestInfo.amountCents -
          pendingRequestInfo.amountCents
        : 0

    const remainingNotInTransitCents =
      outstandingBalanceCents !== undefined
        ? Math.max(
            0,
            outstandingBalanceCents - inTransitRequestInfo.amountCents
          )
        : 0

    return {
      overpaymentThresholdCents,
      remainingNotInTransitCents,
      overpaymentThresholdDollars: overpaymentThresholdCents / 100,
      remainingNotInTransitDollars: remainingNotInTransitCents / 100,
    }
  }, [pendingRequestInfo, inTransitRequestInfo, outstandingBalance])

  return {
    visibleRequests,
    pendingRequestInfo,
    inTransitRequestInfo,
    ...calculations,
  }
}
